import { useUploadMasterProductFamiliesSheetApi } from 'apis/master-product-families';
import type { UploadedFile } from 'design-system';
import {
  Box,
  Button,
  FileUploader,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  VStack,
} from 'design-system';
import React, { useState } from 'react';

export interface IUploadFamiliesSheetModalProps {
  trigger: JSX.Element;
}

const EXCEL_MIME_TYPES = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
] as const;

export const UploadFamiliesSheetModal = ({ trigger }: IUploadFamiliesSheetModalProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [file, setFile] = useState<UploadedFile | null>(null);
  const { mutate: upload, isLoading, error, reset } = useUploadMasterProductFamiliesSheetApi();

  const onCloseModal = () => {
    setFile(null);
    reset();
    onClose();
  };

  const onSubmit = () => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file as File);
    upload({ body: formData }, { onSuccess: onCloseModal });
  };

  const serverErrors = error?.response?.data?.errors ?? [];

  return (
    <>
      {React.cloneElement(trigger, { onClick: onOpen })}
      <Modal
        isOpen={isOpen}
        onClose={onCloseModal}
        size={{ base: 'full', md: 'lg' }}
        scrollBehavior='inside'
        closeOnOverlayClick={false}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Upload Master Product Families Sheet</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align='stretch' spacing='md'>
              <Text fontSize='sm' color='gray.600'>
                Name is the only required column. Rows whose name matches an existing family are updated; unmatched
                names create a new family (Category required in that case).
              </Text>
              <FileUploader
                style='style_01'
                acceptedTypes={[...EXCEL_MIME_TYPES]}
                multiple={false}
                onRemove={() => {
                  setFile(null);
                  reset();
                }}
                onRemoveAll={() => {
                  setFile(null);
                  reset();
                }}
                onUpload={(files: UploadedFile[]) => {
                  setFile(files[0]);
                  reset();
                }}
              />
              {serverErrors.length > 0 && (
                <Box bg='red.50' borderRadius='md' p='sm'>
                  <Text fontSize='sm' color='red.600' fontWeight='semibold'>
                    Upload failed — fix these and try again:
                  </Text>
                  {serverErrors.map((msg, i) => (
                    <Text key={i} fontSize='sm' color='red.600'>
                      {msg}
                    </Text>
                  ))}
                </Box>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant='ghost' colorScheme='gray' onClick={onCloseModal} mr='sm'>
              Cancel
            </Button>
            <Button colorScheme='red' isLoading={isLoading} isDisabled={!file} onClick={onSubmit}>
              Upload
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
